import { HashRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { I18nProvider } from './i18n';
import { AppStateProvider, useApp } from './state/AppState';
import { ThemeProvider } from './state/theme';
import { TabBar } from './components/TabBar';
import { OnboardingFlow } from './features/onboarding/OnboardingFlow';
import { UnlockScreen } from './features/commerce/UnlockScreen';
import { HomeScreen } from './features/home/HomeScreen';
import { AllyScreen } from './features/ally/AllyScreen';
import { ChartsScreen } from './features/charts/ChartsScreen';
import { DiscoverScreen } from './features/discover/DiscoverScreen';
import { FeedScreen } from './features/feed/FeedScreen';
import { ProfileView } from './features/discover/ProfileView';
import { SocialOptInScreen } from './features/social/SocialOptInScreen';
import { MessagesScreen } from './features/messages/MessagesScreen';
import { ConversationView } from './features/messages/ConversationView';
import { ProfileScreen } from './features/profile/ProfileScreen';
import { MemoryScreen } from './features/profile/MemoryScreen';
import { PrivacyScreen } from './features/profile/PrivacyScreen';
import { ProScreen } from './features/commerce/ProScreen';
import { CreditShopScreen } from './features/commerce/CreditShopScreen';
import { RefinementScreen } from './features/profile/RefinementScreen';
import { GlossaryScreen } from './features/glossary/GlossaryScreen';
import { LearnScreen } from './features/learn/LearnScreen';
import { LessonView } from './features/learn/LessonView';
import { HoroscopeScreen } from './features/horoscope/HoroscopeScreen';
import { JournalScreen } from './features/journal/JournalScreen';
import { CompatibilityScreen } from './features/compatibility/CompatibilityScreen';
import { MuhurtaScreen } from './features/muhurta/MuhurtaScreen';

/**
 * `/compatibility/:targetUid` — the same screen, opened against a connection.
 *
 * CompatibilityScreen takes `partnerUid` as a PROP (it never calls useParams),
 * so the URL form needs this three-line adapter rather than a second route
 * pointing at the same component. Keeping the param out of the screen is what
 * lets `/compatibility` and a profile-embedded render share one implementation.
 */
function CompatibilityRoute() {
  const { targetUid } = useParams<{ targetUid: string }>();
  return <CompatibilityScreen partnerUid={targetUid} />;
}

function Gate() {
  const { ready, uid, birthProfile, entitlement } = useApp();
  const location = useLocation();

  if (!ready) {
    return (
      <div className="screen" style={{ display: 'grid', placeItems: 'center' }}>
        <div className="spinner" />
      </div>
    );
  }

  const onboarded = uid && birthProfile;
  // /glossary and /learn are exempt from BOTH gates below: onboarding uses
  // this jargon before an account exists, so "what is a nakshatra?" must not
  // bounce back to step one — and the Learn curriculum has to be readable
  // before an account or a purchase for exactly the same reason.
  const freeLearning =
    location.pathname === '/glossary' ||
    location.pathname === '/learn' ||
    location.pathname.startsWith('/learn/');
  if (!onboarded && location.pathname !== '/onboarding' && !freeLearning) {
    return <Navigate to="/onboarding" replace />;
  }
  if (onboarded && location.pathname === '/onboarding') {
    return <Navigate to="/home" replace />;
  }

  // ₹49 Base gate: onboarded but unpurchased accounts see the unlock screen.
  // (entitlement === null means still loading — don't flash the gate.)
  const locked = onboarded && entitlement !== null && entitlement.tier === 'none';
  const exemptPaths = ['/unlock', '/profile/privacy', '/onboarding'];
  if (locked && !exemptPaths.includes(location.pathname) && !freeLearning) {
    return <Navigate to="/unlock" replace />;
  }

  return (
    <>
      <Routes>
        <Route path="/onboarding" element={<OnboardingFlow />} />
        <Route path="/unlock" element={<UnlockScreen />} />
        <Route path="/home" element={<HomeScreen />} />
        <Route path="/ai" element={<AllyScreen />} />
        <Route path="/ally" element={<Navigate to="/ai" replace />} />
        <Route path="/charts" element={<ChartsScreen />} />
        {/* The four surfaces Home links to that do not own a tab. They sit
            BEHIND the purchase gate deliberately — they are the product, not
            the sample of it, so they are absent from `freeLearning`. */}
        <Route path="/horoscope" element={<HoroscopeScreen />} />
        <Route path="/journal" element={<JournalScreen />} />
        <Route path="/compatibility" element={<CompatibilityScreen />} />
        <Route path="/compatibility/:targetUid" element={<CompatibilityRoute />} />
        <Route path="/muhurta" element={<MuhurtaScreen />} />
        {/* The members-only feed. It holds the tab that used to be Discover's
            (see the note in components/TabBar.tsx); Discover keeps its route
            and is reached from the feed header and from Home. The screen
            itself refuses to fetch anything for a private account, so the
            route is safe to leave open — a deep link explains the feature
            instead of bouncing the reader silently back to Home. */}
        <Route path="/feed" element={<FeedScreen />} />
        <Route path="/discover" element={<DiscoverScreen />} />
        <Route path="/discover/:targetUid" element={<ProfileView />} />
        <Route path="/social/enable" element={<SocialOptInScreen />} />
        <Route path="/messages" element={<MessagesScreen />} />
        <Route path="/messages/:conversationId" element={<ConversationView />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/profile/memory" element={<MemoryScreen />} />
        <Route path="/profile/privacy" element={<PrivacyScreen />} />
        <Route path="/profile/subscription" element={<ProScreen />} />
        <Route path="/pro" element={<Navigate to="/profile/subscription" replace />} />
        <Route path="/profile/credits" element={<CreditShopScreen />} />
        <Route path="/profile/refine" element={<RefinementScreen />} />
        {/* ROUTE ANCHORS — one line per concurrent change, so several agents
            can register a route without racing on the same insertion point.
            Add YOUR route directly under YOUR anchor and nowhere else. */}
        {/* route-anchor: calendar */}
        {/* route-anchor: places */}
        {/* route-anchor: deck */}
        {/* route-anchor: partners */}
        {/* route-anchor: tutorial */}
        <Route path="/glossary" element={<GlossaryScreen />} />
        <Route path="/learn" element={<LearnScreen />} />
        <Route path="/learn/:lessonId" element={<LessonView />} />
        <Route path="*" element={<Navigate to={onboarded ? '/home' : '/onboarding'} replace />} />
      </Routes>
      {onboarded && !locked && <TabBar />}
    </>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AppStateProvider>
          <HashRouter>
            <div className="app-shell">
              <Gate />
            </div>
          </HashRouter>
        </AppStateProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
